import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import axios from "axios";
import "../styles/paymentStatus.css";

interface PaymentResult {
  orderId: string;
  transactionId: string;
  amount: string;
  status: "success" | "failed" | "pending";
  message?: string;
}

const PaymentStatus: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { clearCart } = useCart();

  const orderIdFromQuery = query.get("orderId") || "";
  const fallbackMessage = query.get("message") || "";

  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<PaymentResult | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const directStatus = query.get("status") || "";
  const directAmount = query.get("amount") || "";
  const directMessage = query.get("message") || "";
  const directTransactionId = query.get("transactionId") || "";
  const directPaymentMethod = query.get("paymentMethod") || "";
  const purpose = query.get("purpose") || "";
  const isMarketAnalysisTopup = purpose === "MARKET_ANALYSIS_TOPUP";

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      try {
        // Cash on Delivery or direct frontend success flow
        if (directStatus && !orderIdFromQuery) {
          const fallbackPayment = {
            orderId: `COD-${Date.now()}`,
            transactionId: directTransactionId || "N/A",
            amount: directAmount || "0.00",
            status: directStatus as "success" | "failed" | "pending",
            message: directMessage || "",
          };

          setPayment(fallbackPayment);

          if (directStatus === "success") {
            clearCart();
          }

          setLoading(false);
          return;
        }

        if (!orderIdFromQuery) {
          setErrorMessage(fallbackMessage || "Order reference not found.");
          setLoading(false);
          return;
        }

        const statusEndpoint = isMarketAnalysisTopup
          ? `/market-analysis/payments/status/${encodeURIComponent(orderIdFromQuery)}`
          : `/payments/status/${encodeURIComponent(orderIdFromQuery)}`;

        const response = await axios.get(
          `${
            import.meta.env.VITE_LYRAZONE_BASE_URL ||
            "http://localhost:5000/api"
          }${statusEndpoint}`,
        );

        if (response.data?.success) {
          setPayment(response.data.data);

          const paymentMethodFromQuery = directPaymentMethod.toLowerCase();

          if (
            response.data.data?.status === "success" ||
            paymentMethodFromQuery === "cash on delivery"
          ) {
            clearCart();
          }
        } else {
          setErrorMessage("Unable to fetch payment status.");
        }
      } catch (error: any) {
        setErrorMessage(
          error?.response?.data?.message ||
            fallbackMessage ||
            "Unable to fetch payment status.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
  }, [
    orderIdFromQuery,
    fallbackMessage,
    directStatus,
    directAmount,
    directMessage,
    directTransactionId,
    directPaymentMethod,
  ]);

  const statusConfig = {
    success: {
      icon: "✓",
      title: "Payment Successful",
      message:
        payment?.message ||
        "Your payment has been completed successfully. Thank you for your order.",
      cardClass: "success",
      iconClass: "success-icon",
      textClass: "success-text",
      statusLabel: "Success",
      primaryBtnText: isMarketAnalysisTopup
        ? "Analyze Market Price"
        : "Back to Home",
      primaryBtnLink: isMarketAnalysisTopup ? "/market-price-analysis" : "/",
      secondaryBtnText: isMarketAnalysisTopup
        ? "Get More Access"
        : "Order Status",
      secondaryBtnLink: isMarketAnalysisTopup
        ? "/market-analysis-pricing"
        : "/orders",
    },
    failed: {
      icon: "✕",
      title: "Payment Failed",
      message:
        payment?.message ||
        errorMessage ||
        "Your payment could not be processed. Please try again or use another payment method.",
      cardClass: "failed",
      iconClass: "failed-icon",
      textClass: "failed-text",
      statusLabel: "Failed",
      primaryBtnText: isMarketAnalysisTopup ? "Try Again" : "Try Again",
      primaryBtnLink: isMarketAnalysisTopup
        ? "/market-analysis-pricing"
        : "/checkout",
      secondaryBtnText: isMarketAnalysisTopup
        ? "Back to Market Analysis"
        : "View Cart",
      secondaryBtnLink: isMarketAnalysisTopup
        ? "/market-price-analysis"
        : "/shopping-cart",
    },
    pending: {
      icon: "!",
      title: "Payment Pending",
      message:
        payment?.message ||
        "Your payment is being processed. Please wait while we confirm your transaction.",
      cardClass: "pending",
      iconClass: "pending-icon",
      textClass: "pending-text",
      statusLabel: "Pending",
      primaryBtnText: "Back to Home",
      primaryBtnLink: "/",
      secondaryBtnText: "Order Status",
      secondaryBtnLink: "/orders",
    },
  };

  const resolvedStatus = useMemo(() => {
    if (payment?.status) return payment.status;
    if (errorMessage) return "failed";
    return "pending";
  }, [payment, errorMessage]);

  const currentStatus =
    statusConfig[resolvedStatus as keyof typeof statusConfig] ||
    statusConfig.pending;

  if (loading) {
    return (
      <section className="payment-status-page">
        <div className="container">
          <div className="payment-status-card pending">
            <div className="payment-status-icon pending-icon">!</div>
            <h2>Checking Payment Status</h2>
            <p className="payment-status-message">
              Please wait while we verify your transaction.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="payment-status-page">
      <div className="container">
        <div className={`payment-status-card ${currentStatus.cardClass}`}>
          <div className={`payment-status-icon ${currentStatus.iconClass}`}>
            {currentStatus.icon}
          </div>

          <h2>{currentStatus.title}</h2>
          <p className="payment-status-message">{currentStatus.message}</p>

          <div className="payment-status-details">
            <div className="status-row">
              <span>Order ID</span>
              <strong>{payment?.orderId || orderIdFromQuery || "N/A"}</strong>
            </div>

            <div className="status-row">
              <span>Transaction ID</span>
              <strong>{payment?.transactionId || "N/A"}</strong>
            </div>

            <div className="status-row">
              <span>Amount</span>
              <strong>RM {payment?.amount || "0.00"}</strong>
            </div>

            <div className="status-row">
              <span>Status</span>
              <strong className={currentStatus.textClass}>
                {currentStatus.statusLabel}
              </strong>
            </div>
          </div>

          <div className="payment-status-actions">
            <Link
              to={currentStatus.primaryBtnLink}
              className="payment-btn primary-btn"
            >
              {currentStatus.primaryBtnText}
            </Link>

            <Link
              to={currentStatus.secondaryBtnLink}
              className="payment-btn secondary-btn"
            >
              {currentStatus.secondaryBtnText}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentStatus;
